import React, { useEffect, useMemo, useState } from 'react';
import {
  Autocomplete,
  Box,
  Chip,
  Dialog,
  DialogContent,
  DialogTitle,
  FormHelperText,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import CustomButton from '../../components/@extended/CustomButton';
import { active, pending } from '../../constants/bankAccountStatus';

const parseSubjectName = (subjectEntity) => {
  if (!subjectEntity?.addditionalInfo) return subjectEntity?.email || 'Unknown';
  try {
    const info = JSON.parse(subjectEntity.addditionalInfo);
    return info?.name || subjectEntity.email || 'Unknown';
  } catch {
    return subjectEntity?.email || 'Unknown';
  }
};

const mapSiteSubjects = (subjects = []) =>
  subjects
    .filter((entry) => entry?.subjectEntity?.email)
    .map((entry) => ({
      id: entry.subjectEntity.id,
      email: entry.subjectEntity.email,
      name: parseSubjectName(entry.subjectEntity),
      accountStatus: entry.subjectEntity.accountStatus,
    }));

const getStatusChipColor = (status, theme) => {
  if (active.includes(status)) return theme.palette.success.main;
  if (pending.includes(status)) return theme.palette.warning.main;
  return theme.palette.text.secondary;
};

const isPendingSubject = (subject) => pending.includes(subject?.accountStatus);

const ExecuteEventDialog = ({ open, event, subjects = [], loading, onClose, onConfirm }) => {
  const theme = useTheme();
  const subjectOptions = useMemo(() => mapSiteSubjects(subjects), [subjects]);
  const selectableSubjects = useMemo(
    () => subjectOptions.filter((subject) => !isPendingSubject(subject)),
    [subjectOptions]
  );
  const [selectedSubjects, setSelectedSubjects] = useState([]);
  const [validationError, setValidationError] = useState('');

  useEffect(() => {
    if (!open) {
      setSelectedSubjects([]);
      setValidationError('');
    }
  }, [open]);

  const handleConfirm = () => {
    const eligibleSubjects = selectedSubjects.filter((subject) => !isPendingSubject(subject));
    if (eligibleSubjects.length === 0) {
      setValidationError('Select at least one subject with an active account to execute payments.');
      return;
    }
    onConfirm(eligibleSubjects.map((subject) => subject.email));
  };

  return (
    <Dialog open={open} onClose={loading ? undefined : onClose} fullWidth maxWidth="sm">
      <DialogTitle>Execute Transaction</DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          <strong>Event:</strong> {event?.eventName} ({event?.eventKey})
        </Typography>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Select site-linked subjects. A payment will be triggered for each selected subject, matching the CSV
          payment flow. Subjects with a pending account status cannot be selected.
        </Typography>

        {subjectOptions.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            No subjects linked to this site.
          </Typography>
        ) : (
          <Autocomplete
            multiple
            options={subjectOptions}
            value={selectedSubjects}
            onChange={(_, value) => {
              const eligible = value.filter((subject) => !isPendingSubject(subject));
              setSelectedSubjects(eligible);
              if (eligible.length > 0) setValidationError('');
            }}
            getOptionLabel={(option) => `${option.name} (${option.email})`}
            getOptionDisabled={(option) => isPendingSubject(option)}
            isOptionEqualToValue={(option, value) => option.id === value.id}
            disabled={loading}
            renderOption={(props, option) => {
              const optionDisabled = isPendingSubject(option);
              return (
                <Box
                  component="li"
                  {...props}
                  key={option.id}
                  aria-disabled={optionDisabled}
                  sx={{
                    opacity: optionDisabled ? 0.5 : 1,
                    pointerEvents: optionDisabled ? 'none' : 'auto',
                  }}
                >
                  <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <Typography variant="body2">{option.name}</Typography>
                      <Chip
                        size="small"
                        label={option.accountStatus || 'unknown'}
                        sx={{
                          color: getStatusChipColor(option.accountStatus, theme),
                          borderColor: getStatusChipColor(option.accountStatus, theme),
                        }}
                        variant="outlined"
                      />
                    </Box>
                    <Typography variant="caption" color="text.secondary">
                      {option.email}
                      {optionDisabled ? ' — account pending' : ''}
                    </Typography>
                  </Box>
                </Box>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Subjects"
                placeholder="Select subjects"
                error={Boolean(validationError)}
              />
            )}
          />
        )}

        {validationError && <FormHelperText error>{validationError}</FormHelperText>}

        <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1, mt: 3 }}>
          <CustomButton variant="outlined" size="small" onClick={onClose} disabled={loading}>
            Cancel
          </CustomButton>
          <CustomButton
            variant="contained"
            size="small"
            onClick={handleConfirm}
            disabled={loading || selectableSubjects.length === 0}
          >
            {loading ? 'Executing...' : 'Execute'}
          </CustomButton>
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default ExecuteEventDialog;
